import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent, type ReactNode, type WheelEvent as ReactWheelEvent } from "react";
import { createPortal } from "react-dom";
import { canZoomIn, FITTED, isZoomed, zoomClamped, zoomClicked, zoomContains, type LightboxZoom, type Point } from "./lightboxZoom";

export interface LightboxPosition {
  index: number;
  count: number;
  onStep: (delta: number) => void;
}

export interface ImageLightboxProps {
  /** Null while the image loads, or when it could not be loaded. */
  src: string | null;
  alt: string;
  caption?: string;
  /** Says the image could not be loaded where it would be. */
  failed?: boolean;
  /** Where the image sits among its siblings: ← and → step through them. */
  position?: LightboxPosition;
  /** Buttons added to the header, before Close. */
  actions?: ReactNode;
  /** Drawn inside a positioned container — the chat — instead of over the
   * whole window. */
  contained?: boolean;
  onClose: () => void;
}

/** A click that moves less than this is a click, not a drag. */
const DRAG_SLOP = 5;

/**
 * One image over a dark scrim. A click on the image zooms in about the
 * pointer, twice, and a third goes back to fitting it; dragging or scrolling
 * pans a zoomed image. With siblings, ← and → (or a swipe) step through them.
 * A click off the image, Escape or Close dismisses it. Swift twin:
 * `ImageLightboxView`.
 */
export function ImageLightbox({ src, alt, caption, failed = false, position, actions, contained = false, onClose }: ImageLightboxProps) {
  const overlayRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const [zoom, setZoom] = useState<LightboxZoom>(FITTED);
  const [dragging, setDragging] = useState(false);
  const drag = useRef<{ pointerId: number; startX: number; startY: number; origin: LightboxZoom; moved: boolean } | null>(null);
  // The key handler is installed once; it reads the props it acts on here.
  const latest = useRef({ onClose, position });
  latest.current = { onClose, position };

  // Each image opens fitted.
  useEffect(() => setZoom(FITTED), [src]);

  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    overlayRef.current?.querySelector<HTMLButtonElement>(".lightbox-close")?.focus();
    const onKey = (event: KeyboardEvent) => {
      const { onClose, position } = latest.current;
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
      } else if ((event.key === "ArrowLeft" || event.key === "ArrowRight") && !event.metaKey && !event.altKey && !event.ctrlKey) {
        event.preventDefault();
        position?.onStep(event.key === "ArrowLeft" ? -1 : 1);
      } else if (event.key === "Tab") {
        // Focus stays on the lightbox's own buttons while it is open.
        const buttons = [...(overlayRef.current?.querySelectorAll<HTMLButtonElement>("button:not(:disabled)") ?? [])];
        if (buttons.length === 0) return;
        event.preventDefault();
        const at = buttons.indexOf(document.activeElement as HTMLButtonElement);
        const next = at < 0 ? 0 : (at + (event.shiftKey ? -1 : 1) + buttons.length) % buttons.length;
        buttons[next]?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = previousOverflow;
      previouslyFocused?.focus();
    };
  }, []);

  /** The stage and the image's fitted size — its box before the zoom
   * transform, which `offsetWidth` reports. */
  const geometry = () => {
    const overlay = overlayRef.current;
    const image = imageRef.current;
    if (!overlay || !image) return null;
    const rect = overlay.getBoundingClientRect();
    return { rect, stage: { width: rect.width, height: rect.height }, fitted: { width: image.offsetWidth, height: image.offsetHeight } };
  };
  const pointIn = (event: { clientX: number; clientY: number }, rect: DOMRect): Point => ({
    x: event.clientX - rect.left - rect.width / 2,
    y: event.clientY - rect.top - rect.height / 2,
  });

  const onPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.button !== 0 || (event.target as HTMLElement).closest("button, a")) return;
    drag.current = { pointerId: event.pointerId, startX: event.clientX, startY: event.clientY, origin: zoom, moved: false };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };
  const onPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const current = drag.current;
    if (!current || current.pointerId !== event.pointerId) return;
    const dx = event.clientX - current.startX;
    const dy = event.clientY - current.startY;
    if (!current.moved && Math.hypot(dx, dy) < DRAG_SLOP) return;
    if (!current.moved) {
      current.moved = true;
      setDragging(true);
    }
    const g = geometry();
    if (!g || !isZoomed(current.origin)) return;
    setZoom(zoomClamped({ ...current.origin, x: current.origin.x + dx, y: current.origin.y + dy }, g.fitted, g.stage));
  };
  const onPointerUp = (event: ReactPointerEvent<HTMLDivElement>) => {
    const current = drag.current;
    if (!current || current.pointerId !== event.pointerId) return;
    drag.current = null;
    setDragging(false);
    const dx = event.clientX - current.startX;
    const dy = event.clientY - current.startY;
    if (current.moved) {
      // On a fitted image a sideways swipe steps and a downward one dismisses.
      if (isZoomed(current.origin)) return;
      if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy)) position?.onStep(dx < 0 ? 1 : -1);
      else if (dy > 100 && dy > Math.abs(dx)) onClose();
      return;
    }
    const g = geometry();
    if (!g || !src) return onClose();
    const point = pointIn(event, g.rect);
    if (!zoomContains(zoom, point, g.fitted)) return onClose();
    setZoom(zoomClicked(zoom, point, g.fitted, g.stage));
  };
  const onPointerCancel = () => {
    drag.current = null;
    setDragging(false);
  };
  const onWheel = (event: ReactWheelEvent<HTMLDivElement>) => {
    const g = geometry();
    if (!g || !isZoomed(zoom)) return;
    setZoom((previous) => zoomClamped({ ...previous, x: previous.x - event.deltaX, y: previous.y - event.deltaY }, g.fitted, g.stage));
  };

  const many = position && position.count > 1;
  const overlay = (
    <div
      ref={overlayRef}
      className={`lightbox-overlay${contained ? " is-contained" : ""}${dragging ? " is-dragging" : ""}`}
      role="dialog"
      aria-modal="true"
      aria-label={alt}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerCancel}
      onWheel={onWheel}
    >
      {src ? (
        <img
          ref={imageRef}
          className="lightbox-image"
          src={src}
          alt={alt}
          draggable={false}
          style={{
            transform: `translate(${zoom.x}px, ${zoom.y}px) scale(${zoom.scale})`,
            cursor: dragging && isZoomed(zoom) ? "grabbing" : canZoomIn(zoom) ? "zoom-in" : "zoom-out",
          }}
        />
      ) : failed ? (
        <div className="lightbox-status">{caption ?? "The image"} could not be loaded.</div>
      ) : (
        <span className="spinner lightbox-spinner" role="status" aria-label="Loading image" />
      )}
      <div className="lightbox-header">
        <div className="lightbox-title">
          {caption && <span className="lightbox-caption" title={caption}>{caption}</span>}
          {many && <span className="lightbox-position">{position.index + 1} of {position.count}</span>}
        </div>
        <div className="lightbox-actions">
          {isZoomed(zoom) && (
            <button type="button" className="lightbox-button" aria-label="Fit image" title="Fit image" onClick={() => setZoom(FITTED)}>
              <LightboxIcon d="M14 10l7-7M14 10h5M14 10V5M10 14l-7 7M10 14H5M10 14v5" />
            </button>
          )}
          {actions}
          <button type="button" className="lightbox-button lightbox-close" aria-label="Close image" title="Close (Esc)" onClick={onClose}>
            <LightboxIcon d="M6 6l12 12M18 6L6 18" />
          </button>
        </div>
      </div>
      {many && (
        <>
          <button
            type="button"
            className="lightbox-step lightbox-step-previous"
            aria-label="Previous image"
            title="Previous image (←)"
            disabled={position.index === 0}
            onClick={() => position.onStep(-1)}
          >
            <LightboxIcon d="M15 5l-7 7 7 7" />
          </button>
          <button
            type="button"
            className="lightbox-step lightbox-step-next"
            aria-label="Next image"
            title="Next image (→)"
            disabled={position.index === position.count - 1}
            onClick={() => position.onStep(1)}
          >
            <LightboxIcon d="M9 5l7 7-7 7" />
          </button>
        </>
      )}
    </div>
  );
  return contained ? overlay : createPortal(overlay, document.body);
}

export function LightboxIcon({ d }: { d: string }) {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d={d} />
    </svg>
  );
}
